import { Collapse, List } from "@mui/material";
import Box from "@mui/material/Box";
import { AuthState } from "context/AuthContext";
import MenuIcon from "@mui/icons-material/Menu";
import MuiDrawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";

import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { grey } from "@mui/material/colors";

import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar/Navbar";
import { styled, useTheme } from "@mui/material/styles";
import CategoryIcon from "@mui/icons-material/Category";
import { services } from "data/authData";
import { AnalyticsRounded, ExpandLess, ExpandMore, Inbox, StarBorder } from "@mui/icons-material";
import DrawerListItem from "./DrawerListItem/DrawerListItem";
const drawerWidth = 240;
const miniDrawerWidth = 60;

const serviceIconStyle = { color: grey[300], sx: { transform: "scale(1.3)", p: 2 } };

const listColorSX = {
	// selected and (selected + hover) states
	"&& .Mui-selected, && .Mui-selected:hover": {
		bgcolor: grey[900],
		color: "white",
		"&, & .MuiListItemIcon-root": {
			color: "white",
		},
		"&, & .MuiListItemText-primary": {
			color: "white",
		},
	},
	// hover states
	"& .MuiListItemButton-root:hover": {
		bgcolor: grey[300],
		"&, & .MuiListItemIcon-root": {
			color: grey[800],
		},
	},
};

const openedMixin = (theme) => ({
	width: drawerWidth,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up("sm")]: {
		width: `calc(${theme.spacing(7)} + 1px)`,
	},
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: "nowrap",
	boxSizing: "border-box",
	...(open && {
		...openedMixin(theme),
		"& .MuiDrawer-paper": openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		"& .MuiDrawer-paper": closedMixin(theme),
	}),
}));

const DashboardNavCollapsableWithCustomNav = ({
	drawerList,
	searchParamName,
	navSpace,
	service,
	pathName,
	rootPath,
	mainService,
	dashboardMeta,
}) => {
	// drawer open/close
	const [open, setOpen] = useState(false);
	const [drawerElements, setDrawerElements] = useState();

	const theme = useTheme();

	const { user } = AuthState();

	const SetDrawerElements = () => {
		let service;
		setDrawerElements(
			Object.keys(drawerList)?.reduce((acc, row) => {
				if (drawerList[row].display) acc.push(drawerList[row]);
				if (row === pathName) service = { ...drawerList[row] };
				return acc;
			}, [])
		);

		setOpen(false);
	};

	useEffect(() => {
		SetDrawerElements();
	}, []);

	return (
		<Box width="100%">
			<Navbar />
			<Box display="flex" justifyContent="space-between">
				<Box
					sx={{
						width: (theme) => `calc(${theme.spacing(7)} + 1px)`,
						[theme.breakpoints.up("sm")]: {
							width: `calc(${theme.spacing(7)} + 1px)`,
						},
					}}>
					<Drawer
						variant="permanent"
						open={open}
						onMouseEnter={() => setOpen(true)}
						onMouseLeave={() => setOpen(false)}
						sx={{
							width: `${open ? drawerWidth : miniDrawerWidth}px`,
							flexShrink: 0,
							whiteSpace: "nowrap",
							boxSizing: "border-box",
							"& .MuiDrawer-paper": {
								width: `${open ? drawerWidth : miniDrawerWidth}px`,
								transition: (theme) =>
									theme.transitions.create("width", {
										easing: theme.transitions.easing.sharp,
										duration: theme.transitions.duration.enteringScreen,
									}),
								overflowX: "hidden",
							},
						}}>
						<Box
							display="flex"
							justifyContent={open ? "flex-start" : "center"}
							alignItems="center"
							position="sticky"
							pt={9}
							pl={open ? 2 : 0}
							pb={1}>
							{service?.icon}
							{/* <AnalyticsRounded fontSize="large" /> */}
							<Typography pl={3} variant="h6" display={open ? "block" : "none"} noWrap>
								{service?.name}
							</Typography>
						</Box>

						<Divider />
						<Box
							id="drawer-scroll-container"
							overflow="hidden"
							sx={{
								overflowY: open ? "auto" : "hidden",
								height: "calc(100vh - 120px)",
								scrollbarWidth: "thin",
							}}>
							<List sx={listColorSX}>
								{drawerElements &&
									Object.keys(drawerElements)?.map((key, index) => {
										const service = drawerElements[key];
										return (
											<DrawerListItem
												key={service.key}
												service={service}
												currentPage={pathName}
												rootPath={rootPath}
												open={open}
												listSX={listColorSX}
												parentPath={mainService ?? dashboardMeta.path}
											/>
										);
									})}
							</List>
						</Box>
					</Drawer>
				</Box>
				<Box
					component="main"
					sx={{
						flexGrow: 1,
						boxSizing: "border-box",
						width: `calc(100% - ${miniDrawerWidth}px)`,
						p: 1,
						transition: (theme) =>
							theme.transitions.create("width", {
								easing: theme.transitions.easing.sharp,
								duration: theme.transitions.duration.enteringScreen,
							}),
					}}>
					<Box component="section" pt={navSpace || 0} />
					<Outlet context={[pathName, service]} />
				</Box>
			</Box>
		</Box>
	);
};

export default DashboardNavCollapsableWithCustomNav;
